import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Route, Plus, Trash2, ArrowUp, ArrowDown, X, Save, Users, Loader2, GripVertical } from "lucide-react";
import api from "@/lib/api";

const inputCls = "w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 placeholder:text-muted-foreground/50 focus:outline-none focus:border-cadmium/50 transition-colors";
const labelCls = "block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5";
const emptyForm = { name: "", description: "", course_ids: [] };

export const LearningPaths = ({ courses = [], employees = [] }) => {
  const [paths, setPaths] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pickCourse, setPickCourse] = useState("");
  const [assignFor, setAssignFor] = useState(null);
  const [picks, setPicks] = useState(new Set());
  const [assignDue, setAssignDue] = useState("");
  const [assigning, setAssigning] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/training/paths");
      setPaths(res.data);
    } catch {
      toast.error("Could not load learning paths");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const courseTitle = (id) => courses.find((c) => c.id === id)?.title || "(course removed)";

  const addStep = () => {
    if (!pickCourse || form.course_ids.includes(pickCourse)) return;
    setForm((f) => ({ ...f, course_ids: [...f.course_ids, pickCourse] }));
    setPickCourse("");
  };
  const removeStep = (id) => setForm((f) => ({ ...f, course_ids: f.course_ids.filter((c) => c !== id) }));
  const move = (i, dir) => {
    setForm((f) => {
      const arr = [...f.course_ids];
      const j = i + dir;
      if (j < 0 || j >= arr.length) return f;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return { ...f, course_ids: arr };
    });
  };

  const resetForm = () => { setForm(emptyForm); setEditingId(null); setPickCourse(""); };

  const save = async () => {
    if (form.name.trim().length < 3) { toast.error("Give the path a name (3+ characters)"); return; }
    if (form.course_ids.length === 0) { toast.error("Add at least one course to the path"); return; }
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), description: form.description.trim(), course_ids: form.course_ids, published: true };
      if (editingId) {
        await api.patch(`/training/paths/${editingId}`, payload);
        toast.success("Path updated");
      } else {
        await api.post("/training/paths", payload);
        toast.success("Learning path created");
      }
      resetForm();
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const editPath = (p) => {
    setEditingId(p.id);
    setForm({ name: p.name, description: p.description || "", course_ids: [...p.course_ids] });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deletePath = async (p) => {
    if (!window.confirm(`Delete the "${p.name}" path? Employees already enrolled keep their current courses.`)) return;
    try {
      await api.delete(`/training/paths/${p.id}`);
      toast.success("Path deleted");
      if (editingId === p.id) resetForm();
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const openAssign = (p) => { setAssignFor(p); setPicks(new Set()); setAssignDue(""); };
  const togglePick = (id) => setPicks((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const runAssign = async () => {
    if (picks.size === 0) { toast.error("Pick at least one employee"); return; }
    setAssigning(true);
    try {
      const res = await api.post(`/training/paths/${assignFor.id}/assign`, { employee_ids: [...picks], due_date: assignDue || null });
      toast.success(`Enrolled ${res.data.enrolled} employee(s) in "${assignFor.name}"`, {
        description: `First course assigned to ${res.data.assigned}. ${res.data.email_failed?.length ? `Email failed for ${res.data.email_failed.length}.` : "The next course unlocks automatically when each one is passed."}`,
      });
      setAssignFor(null);
      setPicks(new Set());
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Assign failed");
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="learning-paths">
      {/* Builder */}
      <div className="technical-card p-6" data-testid="lp-builder">
        <div className="flex items-center gap-2 mb-1">
          <Route size={14} className="text-sky-600" />
          <p className="text-xs font-mono font-semibold uppercase tracking-[0.2em] text-slate-900">{editingId ? "Edit learning path" : "Build a learning path"}</p>
        </div>
        <p className="text-xs text-muted-foreground mb-4">Sequence courses for new hires. Only the first course is assigned up front; each next course unlocks automatically when the previous one is passed.</p>

        <label className={labelCls}>Path name</label>
        <input data-testid="lp-name" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. New Leasing Agent Onboarding" />

        <label className={`${labelCls} mt-3`}>Description (optional)</label>
        <textarea data-testid="lp-desc" className={inputCls} rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Who is this path for and what does it cover?" />

        <label className={`${labelCls} mt-3`}>Add courses in order</label>
        <div className="flex gap-2">
          <select data-testid="lp-course-pick" className={inputCls} value={pickCourse} onChange={(e) => setPickCourse(e.target.value)}>
            <option value="">Select a course…</option>
            {courses.filter((c) => !form.course_ids.includes(c.id)).map((c) => (
              <option key={c.id} value={c.id}>{c.title}{c.category ? ` · ${c.category}` : ""}</option>
            ))}
          </select>
          <button data-testid="lp-add-step" onClick={addStep} disabled={!pickCourse} className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-sky-600 px-3 py-2 text-xs font-mono uppercase tracking-wider text-white hover:bg-sky-700 disabled:opacity-40 transition-colors">
            <Plus size={13} /> Add
          </button>
        </div>

        <div className="mt-3 space-y-2" data-testid="lp-steps">
          {form.course_ids.length === 0 && <p className="text-xs text-muted-foreground/70 italic">No courses yet — add them above to form the sequence.</p>}
          {form.course_ids.map((id, i) => (
            <div key={id} className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2" data-testid={`lp-step-${i}`}>
              <GripVertical size={13} className="text-slate-300" />
              <span className="font-mono text-xs text-sky-600 w-5">{i + 1}.</span>
              <span className="flex-1 text-sm text-slate-900 truncate">{courseTitle(id)}</span>
              <button onClick={() => move(i, -1)} disabled={i === 0} title="Move up" className="text-slate-400 hover:text-sky-600 disabled:opacity-30"><ArrowUp size={13} /></button>
              <button onClick={() => move(i, 1)} disabled={i === form.course_ids.length - 1} title="Move down" className="text-slate-400 hover:text-sky-600 disabled:opacity-30"><ArrowDown size={13} /></button>
              <button onClick={() => removeStep(id)} title="Remove" data-testid={`lp-remove-step-${i}`} className="text-slate-400 hover:text-red-600"><X size={13} /></button>
            </div>
          ))}
        </div>

        <div className="mt-4 flex gap-2">
          <button data-testid="lp-save" onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-md bg-sky-600 px-4 py-2 text-xs font-mono uppercase tracking-widest text-white hover:bg-sky-700 disabled:opacity-50 transition-colors">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} {editingId ? "Save changes" : "Create path"}
          </button>
          {editingId && <button data-testid="lp-cancel-edit" onClick={resetForm} className="rounded-md border border-slate-300 px-4 py-2 text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-slate-900 transition-colors">Cancel</button>}
        </div>
      </div>

      {/* Paths list */}
      <div className="space-y-3" data-testid="lp-list">
        <p className="text-xs font-mono font-semibold uppercase tracking-[0.2em] text-slate-900 mb-1">Learning paths ({paths.length})</p>
        {paths.length === 0 && <div className="technical-card p-6 text-sm text-muted-foreground" data-testid="lp-empty">No paths yet — build one on the left.</div>}
        {paths.map((p) => (
          <div key={p.id} className="technical-card p-5" data-testid={`lp-card-${p.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900">{p.name}</p>
                {p.description && <p className="text-xs text-muted-foreground mt-0.5">{p.description}</p>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => openAssign(p)} data-testid={`lp-assign-${p.id}`} title="Assign to employees" className="inline-flex items-center gap-1 rounded-md border border-sky-200 bg-sky-50 px-2.5 py-1.5 text-[10px] font-mono uppercase tracking-wider text-sky-700 hover:bg-sky-100 transition-colors"><Users size={12} /> Assign</button>
                <button onClick={() => editPath(p)} data-testid={`lp-edit-${p.id}`} title="Edit path" className="text-slate-400 hover:text-sky-600"><Save size={13} /></button>
                <button onClick={() => deletePath(p)} data-testid={`lp-delete-${p.id}`} title="Delete path" className="text-slate-400 hover:text-red-600"><Trash2 size={13} /></button>
              </div>
            </div>
            <ol className="mt-3 space-y-1">
              {p.steps.map((s, i) => (
                <li key={s.id + i} className="flex items-center gap-2 text-xs" data-testid={`lp-card-step-${p.id}-${i}`}>
                  <span className="font-mono text-sky-600 w-5">{i + 1}.</span>
                  <span className="text-slate-700 truncate">{s.title}</span>
                  {s.category && <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground/70">{s.category}</span>}
                </li>
              ))}
            </ol>
          </div>
        ))}
      </div>

      {/* Assign modal */}
      {assignFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setAssignFor(null)} data-testid="lp-assign-modal">
          <div className="technical-card w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-slate-900">Assign "{assignFor.name}"</p>
              <button onClick={() => setAssignFor(null)} className="text-slate-400 hover:text-slate-900"><X size={16} /></button>
            </div>
            <p className="text-xs text-muted-foreground mb-3">Enrolled employees get the first course now; each next course unlocks when they pass the one before it.</p>
            <label className={labelCls}>Due date for the first course (optional)</label>
            <input type="date" data-testid="lp-assign-due" className={`${inputCls} mb-3`} value={assignDue} onChange={(e) => setAssignDue(e.target.value)} />
            <label className={labelCls}>Employees ({picks.size} selected)</label>
            <div className="max-h-56 overflow-y-auto rounded-md border border-slate-200 divide-y divide-slate-100">
              {employees.length === 0 && <p className="p-3 text-xs text-muted-foreground">No employees yet.</p>}
              {employees.map((e) => (
                <label key={e.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50" data-testid={`lp-pick-${e.id}`}>
                  <input type="checkbox" checked={picks.has(e.id)} onChange={() => togglePick(e.id)} />
                  <span className="text-slate-900">{e.name}</span>
                  <span className="text-[11px] text-muted-foreground">{e.email}</span>
                </label>
              ))}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setAssignFor(null)} className="rounded-md border border-slate-300 px-4 py-2 text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-slate-900">Cancel</button>
              <button data-testid="lp-assign-confirm" onClick={runAssign} disabled={assigning} className="inline-flex items-center gap-2 rounded-md bg-sky-600 px-4 py-2 text-xs font-mono uppercase tracking-widest text-white hover:bg-sky-700 disabled:opacity-50">
                {assigning ? <Loader2 size={13} className="animate-spin" /> : <Users size={13} />} Enroll
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
